<div class="my-cases-page">
  <header class="page-header">
    <div class="page-title-wrap">
      <h1>My Cases</h1>
    </div>
  </header>

  <div class="layout" [class.layout--no-case]="!activeHandoffId()">
    <op-case-list
      [cases]="caseList()"
      [searchQuery]="searchQuery()"
      (caseSelected)="selectCase($event)"
      (searchQueryChange)="searchQuery.set($event)"
    />

    <op-case-chat
      [messages]="messages()"
      [firstUnreadMessageId]="firstUnreadMessageId()"
      [loading]="loading()"
      [demoMode]="demoMode()"
      [conversationId]="activeConversationId"
      [activeHandoffId]="activeHandoffId()"
      [customerName]="customerName()"
      [customerInitials]="customerInitials()"
      [customerPhone]="customerPhone()"
      [primaryCaseCode]="primaryCaseCode()"
      [pastOrdersRoute]="pastOrdersRoute()"
      (muteAction)="onMuteSelectionChange($event)"
      (demoMessageSent)="onDemoMessageSent($event)"
    />

    @if (activeHandoffId()) {
    <op-case-sidebar
      [handoff]="handoff()"
      [orders]="orders()"
      [ordersLoading]="ordersLoading()"
      [ordersSaving]="ordersSaving()"
      [demoMode]="demoMode()"
      [returningToAi]="returningToAi()"
      [orderStatusOptions]="orderStatusOptions"
      [serviceModelOptions]="serviceModelOptions"
      (createOrderClicked)="openCreateOrderDrawer()"
      (editOrder)="openEditOrderDrawer($event)"
      (showSummary)="openOrderSummary($event)"
      (statusChange)="saveOrderStatus($event.order, $event.status)"
      (returnToAiClicked)="openReturnToAiModal()"
    />
    }
  </div>

  <div class="drawer-shell" [class.open]="createOrderDrawerOpen()">
    @if (createOrderDrawerOpen()) {
      <button
        type="button"
        class="drawer-backdrop"
        (click)="minimizeCreateOrderDrawer()"
        aria-label="Minimize order draft"></button>
    }

    <aside
      class="create-order-drawer"
      [class.open]="createOrderDrawerOpen()"
      [class.minimized]="createOrderDrawerMinimized()"
      [class.restoring]="createOrderDrawerRestoring()"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-order-title"
      (keydown.escape)="minimizeCreateOrderDrawer()">
      <div class="drawer-header">
        <div>
          @if (editingOrder(); as eo) {
            <h2 id="create-order-title">Edit order · {{ eo.order_code }}</h2>
          } @else {
            <h2 id="create-order-title">Create order from case</h2>
            <p>Fill in the order details based on the case summary below.</p>
          }
        </div>

        <button
          type="button"
          class="drawer-minimize-btn"
          (click)="minimizeCreateOrderDrawer()"
          matTooltip="Minimize — keeps everything you entered"
          aria-label="Minimize order draft">
          <mat-icon aria-hidden="true">remove</mat-icon>
        </button>
      </div>

      <mat-divider></mat-divider>

      <div class="drawer-customer-card">
        <div class="drawer-customer-title">{{ customerName() }} · {{ customerPhone() }}</div>
        @if (handoff(); as h) {
          <op-handoff-summary-card class="drawer-summary" [row]="h" />
        }
      </div>

      <div class="drawer-form">
        <div class="form-grid order-time-grid">
          <mat-form-field appearance="outline" floatLabel="always" class="full-width">
            <mat-label>Service Model *</mat-label>
            <mat-select [(ngModel)]="createOrderDraft.service_model" name="drawerServiceModel">
              @for (opt of serviceModelOptions; track opt.value) {
                <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" floatLabel="always" class="full-width">
            <mat-label>Payment Type</mat-label>
            <mat-select [(ngModel)]="createOrderDraft.payment_type" name="drawerPaymentType">
              <mat-option value="">— Not set —</mat-option>
              @for (opt of paymentTypeOptions; track opt.value) {
                <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
              }
            </mat-select>
            <mat-icon matIconSuffix class="field-hint-icon"
              matTooltip='Deposit / full / installment. Stripe link is generated at "waiting for payment".'
              matTooltipPosition="above">info_outline</mat-icon>
          </mat-form-field>
        </div>

        <div class="form-grid order-time-grid">
          <mat-form-field appearance="outline" floatLabel="always" class="full-width">
            <mat-label>Quote amount (CAD)</mat-label>
            <input matInput type="number" min="0" step="0.01" [(ngModel)]="createOrderDraft.quote_amount" name="drawerQuoteAmount" placeholder="0.00" />
            <mat-icon matIconSuffix class="field-hint-icon"
              matTooltip="Pre-confirmation estimate. Separate from the final payment amount."
              matTooltipPosition="above">info_outline</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline" floatLabel="always" class="full-width">
            <mat-label>Estimated duration (min)</mat-label>
            <input matInput type="number" min="1" step="1" [(ngModel)]="createOrderDraft.estimated_duration_minutes" name="drawerEstimatedDuration" placeholder="e.g. 60" />
          </mat-form-field>
        </div>

        <div class="form-grid order-time-grid">
          <mat-form-field appearance="outline" floatLabel="always" class="full-width">
            <mat-label>Category</mat-label>
            <mat-select
              [(ngModel)]="createOrderDraft.category"
              name="drawerCategory"
              (ngModelChange)="onDrawerCategoryChange()">
              <mat-option value="">— Not specified —</mat-option>
              @for (cat of categories(); track cat.category_id) {
                <mat-option [value]="cat.name">{{ cat.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" floatLabel="always" class="full-width">
            <mat-label>Service</mat-label>
            <input matInput [(ngModel)]="createOrderDraft.service_name" name="drawerService" />
          </mat-form-field>
        </div>

        <div class="form-grid order-time-grid">
          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Order Date</mat-label>
            <input
              matInput
              [matDatepicker]="scheduledDatePicker"
              [(ngModel)]="createOrderDraft.scheduledDate"
              name="drawerScheduledDate"
              placeholder="MM/DD/YYYY"
            />
            <mat-datepicker-toggle matSuffix [for]="scheduledDatePicker" />
            <mat-datepicker #scheduledDatePicker />
          </mat-form-field>

          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Order Time</mat-label>
            <input
              matInput
              [matTimepicker]="scheduledTimePicker"
              [(ngModel)]="createOrderDraft.scheduledTime"
              name="drawerScheduledTime"
              placeholder="12:00 PM"
            />
            <mat-timepicker-toggle matSuffix [for]="scheduledTimePicker" />
            <mat-timepicker #scheduledTimePicker interval="30min" />
          </mat-form-field>
        </div>

        <div class="form-grid order-time-grid">
          <mat-form-field appearance="outline" floatLabel="always" class="full-width">
            <mat-label>Provider</mat-label>
            <input
              matInput
              [matAutocomplete]="drawerProviderAuto"
              [(ngModel)]="createOrderDraft.providerDisplayText"
              name="drawerProvider"
              placeholder="Search provider or leave unassigned"
            />
            <mat-autocomplete #drawerProviderAuto="matAutocomplete" (optionSelected)="selectDrawerProvider($event.option.value)">
              @for (provider of providerDrawerResults(); track provider.id) {
                <mat-option [value]="provider">
                  <span class="drawer-provider-name">{{ provider.name }}</span>
                  @if (isRecommendedProvider(provider)) {
                    <span class="drawer-recommended-chip">Recommended</span>
                  }
                </mat-option>
              }
              @if (providersCategoryMissing()) {
                <mat-option disabled class="drawer-empty-option">
                  No providers for this category in this city — use Unknown or Other (notes) below.
                </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline" floatLabel="always" class="full-width">
            <mat-label>Service address</mat-label>
            <input matInput [(ngModel)]="createOrderDraft.address" name="drawerAddressLine" placeholder="e.g. 123 Main St NW" />
            <mat-hint>Optional — where the service will be performed</mat-hint>
          </mat-form-field>
        </div>

        <div class="shortcut-row">
          <button type="button" class="shortcut-chip" (click)="applyDrawerProviderShortcut('Assign later')">Assign later</button>
          <button type="button" class="shortcut-chip" (click)="applyDrawerProviderShortcut('Unknown')">Unknown</button>
          <button type="button" class="shortcut-chip" (click)="applyDrawerProviderShortcut('Other (in notes)')">Other (notes)</button>
        </div>

        <div class="form-grid order-time-grid">
          <mat-form-field appearance="outline" class="full-width notes-field">
            <mat-label>Access notes</mat-label>
            <textarea matInput rows="3" [(ngModel)]="createOrderDraft.address_access_notes" name="drawerAddress"></textarea>
            <mat-hint>Entry code, parking, contact on-site, etc.</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width notes-field">
            <mat-label>Internal Notes</mat-label>
            <textarea matInput rows="3" [(ngModel)]="createOrderDraft.internal_notes" name="drawerNotes"></textarea>
          </mat-form-field>
        </div>

        <div class="form-grid order-time-grid">
          <mat-form-field appearance="outline" class="full-width notes-field">
            <mat-label>Completion notes</mat-label>
            <textarea matInput rows="3" [(ngModel)]="createOrderDraft.completion_notes" name="drawerCompletionNotes"></textarea>
          </mat-form-field>

        </div>

        @if (createOrderError()) {
          <div class="drawer-error">{{ createOrderError() }}</div>
        }

        @if (editingOrder(); as eo) {
          <mat-divider></mat-divider>

          <!-- Documents -->
          <div class="drawer-edit-section">
            <div class="field-label-row">
              <span class="field-label">Documents</span>
            </div>
            <input
              class="visually-hidden"
              type="file"
              #drawerInvoiceInput
              accept="application/pdf,image/jpeg,image/png,image/webp"
              (change)="onInvoiceSelected(eo, $event)"
            />
            <button
              type="button"
              class="shortcut-chip"
              (click)="drawerInvoiceInput.click()"
              [disabled]="orderDocumentsUploading()[eo.id]"
            >
              {{ orderDocumentsUploading()[eo.id] ? 'Uploading…' : 'Upload file' }}
            </button>

            @if (orderDocumentsError()[eo.id]; as docErr) {
              <div class="drawer-error">{{ docErr }}</div>
            }

            @if (documentsFor(eo.id).length === 0 && !orderDocumentsLoading()[eo.id]) {
              <div class="file-name">No documents uploaded</div>
            } @else {
              <ul class="document-list">
                @for (doc of documentsFor(eo.id); track doc.id) {
                  <li class="document-item">
                    <button type="button" class="document-link" (click)="openDocument(doc)" [title]="doc.original_filename">
                      {{ doc.original_filename }}
                    </button>
                    <button type="button" class="document-remove-btn" (click)="removeDocument(doc)" aria-label="Remove document">✕</button>
                  </li>
                }
              </ul>
            }
          </div>

          <!-- Payment -->
          <div class="drawer-edit-section">
            <div class="field-label-row">
              <span class="field-label">Payment</span>
            </div>
            @if (eo.stripe_payment_link_url) {
              <div class="payment-link-block">
                <a class="payment-link-url" [href]="eo.stripe_payment_link_url" target="_blank" rel="noopener">
                  {{ eo.stripe_payment_link_url }}
                </a>
              </div>
            }
            <button
              type="button"
              class="shortcut-chip"
              [disabled]="!canSendPayment(eo) || createOrderSaving()"
              (click)="sendPayment(eo)"
            >
              {{ eo.stripe_payment_link_url ? 'Refresh Payment Link' : 'Send Payment' }}
            </button>
          </div>

          <!-- Status history -->
          <div class="drawer-edit-section">
            <div class="field-label-row">
              <span class="field-label">Status history</span>
            </div>
            @if (historyFor(eo.id).length === 0 && !orderHistoryLoading()[eo.id]) {
              <div class="file-name">No status changes recorded yet.</div>
            } @else {
              <ul class="drawer-history-list">
                @for (entry of historyFor(eo.id); track entry.id) {
                  <li>{{ entry.from_status ? entry.from_status + ' → ' : '' }}{{ entry.to_status }}
                    <span class="drawer-history-meta">· {{ entry.created_at | date: 'MMM d, y, HH:mm' }}</span>
                  </li>
                }
              </ul>
            }
          </div>
        }
      </div>

      <mat-divider></mat-divider>

      <div class="drawer-actions">
        <button type="button" class="drawer-cancel-btn" (click)="requestDiscardDraft()">
          Cancel
        </button>
        @if (editingOrder()) {
          <button type="button" class="primary-blue-btn drawer-action-btn" [disabled]="createOrderSaving()" (click)="saveEditOrder()">
            {{ createOrderSaving() ? 'Saving…' : 'Save changes' }}
          </button>
        } @else {
          <button type="button" class="primary-blue-btn drawer-action-btn" [disabled]="createOrderSaving()" (click)="saveCreateOrder()">
            {{ createOrderSaving() ? 'Saving…' : 'Create & Open' }}
          </button>
        }
      </div>
    </aside>
  </div>

  @if (createOrderDrawerMinimized()) {
    <op-order-draft-pill
      class="order-draft-pill"
      [label]="draftLabel()"
      [dirty]="draftDirty()"
      (resume)="resumeCreateOrderDrawer()"
      (discard)="requestDiscardDraft()"
    />
  }
</div>

@if (pendingCaseSwitch()) {
  <div class="confirm-backdrop" (click)="cancelCaseSwitch()" aria-hidden="true"></div>

  <div class="confirm-modal" role="dialog" aria-modal="true" aria-labelledby="case-switch-title">
    <h3 id="case-switch-title" class="confirm-title">Unsaved order draft</h3>
    <p class="confirm-message">
      Your draft for this case has changes that were never saved. Opening another case discards it.
    </p>

    <div class="confirm-actions">
      <button type="button" class="drawer-cancel-btn" (click)="cancelCaseSwitch()">
        Keep editing
      </button>
      <button
        mat-flat-button
        type="button"
        class="confirm-btn confirm-btn--danger"
        (click)="confirmCaseSwitch()"
      >
        Discard & switch
      </button>
    </div>
  </div>
}

@if (discardConfirmOpen()) {
  <div class="confirm-backdrop" (click)="cancelDiscardDraft()" aria-hidden="true"></div>

  <div class="confirm-modal" role="dialog" aria-modal="true" aria-labelledby="discard-draft-title">
    <h3 id="discard-draft-title" class="confirm-title">Discard order draft</h3>
    <p class="confirm-message">
      Everything you entered for this order will be lost. This cannot be undone.
    </p>

    <div class="confirm-actions">
      <button type="button" class="drawer-cancel-btn" (click)="cancelDiscardDraft()">
        Keep editing
      </button>
      <button
        mat-flat-button
        type="button"
        class="confirm-btn confirm-btn--danger"
        (click)="confirmDiscardDraft()"
      >
        Discard
      </button>
    </div>
  </div>
}

@if (returnModalOpen()) {
  <div class="confirm-backdrop" (click)="closeReturnToAiModal()" aria-hidden="true"></div>

  <div class="confirm-modal" role="dialog" aria-modal="true" aria-labelledby="return-modal-title">
    <h3 id="return-modal-title" class="confirm-title">Return to Eldara</h3>

    <div class="return-confirm-checks">
      <label class="return-check-label">
        <input type="checkbox" [checked]="returnConfirm1()" (change)="returnConfirm1.set($any($event.target).checked)" />
        <span>Confirm to connect back to Eldara AI</span>
      </label>
      <label class="return-check-label">
        <input type="checkbox" [checked]="returnConfirm2()" (change)="returnConfirm2.set($any($event.target).checked)" />
        <span>Confirm again</span>
      </label>
    </div>

    @if (returnToAiError()) {
      <div class="return-error">{{ returnToAiError() }}</div>
    }

    <div class="confirm-actions">
      <button type="button" class="drawer-cancel-btn" [disabled]="returningToAi()" (click)="closeReturnToAiModal()">
        Cancel
      </button>
      <button
        mat-flat-button
        type="button"
        class="confirm-btn"
        [disabled]="!returnConfirmed() || returningToAi()"
        (click)="returnToAi()"
      >
        {{ returningToAi() ? 'Returning…' : 'Confirm' }}
      </button>
    </div>
  </div>
}

@if (pendingMuteAction()) {
  <div class="confirm-backdrop" (click)="cancelMuteAction()" aria-hidden="true"></div>

  <div class="confirm-modal" role="dialog" aria-modal="true">
    <p class="confirm-message">{{ muteConfirmMessage() }}</p>

    <div class="confirm-actions">
      <button type="button" class="drawer-cancel-btn" (click)="cancelMuteAction()">
        Cancel
      </button>
      <button
        mat-flat-button
        type="button"
        class="confirm-btn"
        [class.confirm-btn--danger]="pendingMuteAction() === 'block'"
        (click)="confirmMuteAction()"
      >
        {{ pendingMuteAction() === 'block' ? 'Block' : 'Mute' }}
      </button>
    </div>
  </div>
}

@if (summaryOrder(); as so) {
  <div class="confirm-backdrop" (click)="closeOrderSummary()" aria-hidden="true"></div>

  <div class="confirm-modal order-summary-modal" role="dialog" aria-modal="true" aria-labelledby="order-summary-title">
    <h3 id="order-summary-title" class="confirm-title">Order summary · {{ so.order_code }}</h3>

    <dl class="order-summary-list">
      @for (line of summaryLines(); track line.label) {
        <div class="order-summary-row">
          <dt>{{ line.label }}</dt>
          <dd>{{ line.value }}</dd>
        </div>
      }
    </dl>

    <div class="confirm-actions">
      <button type="button" class="drawer-cancel-btn" (click)="closeOrderSummary()">Close</button>
    </div>
  </div>
}
